import React, { forwardRef, useState } from "react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface TextAreaProps
  extends
    BaseComponentProps,
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      disabled,
      className,
      "data-testid": dataTestId,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const borderClass = error
      ? "border-red-500"
      : isFocused
        ? "border-red-500 shadow-[inset_0_0_0_1px_#ef4444]"
        : "border-gray-300 hover:border-gray-400";

    return (
      <Box className={className} data-testid={dataTestId}>
        {label && (
          <Box
            as="label"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Box>
        )}

        <textarea
          ref={ref}
          required={required}
          disabled={disabled}
          className={cn(
            "w-full px-4 py-3 border rounded-lg bg-white transition-colors outline-none resize-none",
            "disabled:bg-gray-100 disabled:cursor-not-allowed",
            borderClass,
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />

        {/* Helper Text or Error */}
        {(helperText || error) && (
          <Box
            as="p"
            className={cn(
              "mt-1 text-sm",
              error ? "text-red-500" : "text-gray-500",
            )}
          >
            {error || helperText}
          </Box>
        )}
      </Box>
    );
  },
);

TextArea.displayName = "TextArea";
